import { useMemo } from "react";

import {
  AgencyType,
  getAgenciesGeographiesAgenciesGet,
  getZonesGeographiesZonesGet,
  getRegionsGeographiesRegionsGet,
  ZoneType,
  RegionType,
} from "@/codegen/openapi2";
import {
  getAgenciesGeographiesAgenciesGetQueryKey,
  getZonesGeographiesZonesGetQueryKey,
  getRegionsGeographiesRegionsGetQueryKey,
} from "@/codegen/openapi2/@tanstack/react-query.gen";

import { queryClient, TWENTY_FOUR_STALE_WHILE_REVALIDATE } from "../react-query";

import { TerritoriesType } from "./types";

const getAgenciesQueryFn = (): Promise<AgencyType[]> =>
  getAgenciesGeographiesAgenciesGet({}).then(response => response.data as AgencyType[]);

export const getAgencies = () => {
  return queryClient.fetchQuery({
    ...TWENTY_FOUR_STALE_WHILE_REVALIDATE,
    queryKey: getAgenciesGeographiesAgenciesGetQueryKey(),
    queryFn: getAgenciesQueryFn,
  });
};

const getZonesQueryFn = (): Promise<ZoneType[]> =>
  getZonesGeographiesZonesGet({}).then(response => response.data as ZoneType[]);

export const getZones = () => {
  return queryClient.fetchQuery({
    queryKey: getZonesGeographiesZonesGetQueryKey(),
    queryFn: getZonesQueryFn,
    ...TWENTY_FOUR_STALE_WHILE_REVALIDATE,
  });
};

const getRegionsQueryFn = (): Promise<RegionType[]> =>
  getRegionsGeographiesRegionsGet({}).then(response => response.data as RegionType[]);

export const getRegions = () => {
  return queryClient.fetchQuery({
    queryKey: getRegionsGeographiesRegionsGetQueryKey(),
    queryFn: getRegionsQueryFn,
    ...TWENTY_FOUR_STALE_WHILE_REVALIDATE,
  });
};

export const getTerritories = async (): Promise<TerritoriesType> =>
  Promise.all([getAgencies(), getZones(), getRegions()]).then(([agencies, zones, regions]) => {
    return {
      agencies,
      zones,
      regions,
    };
  });

interface WithLegalEntityUuid {
  legal_entity_uuid: string;
}

export const useTerritoriesPromise = (legalEntityUuid?: string): Promise<TerritoriesType> => {
  return useMemo(() => {
    const filter = legalEntityUuid
      ? (a: WithLegalEntityUuid) => a.legal_entity_uuid === legalEntityUuid
      : () => true;
    return getTerritories().then(territories => ({
      agencies: territories.agencies.filter(filter),
      zones: territories.zones.filter(filter),
      regions: territories.regions.filter(filter),
    }));
  }, [legalEntityUuid]);
};
